import { Composition } from "../../engine/composition";
import type { hex } from "../../engine/types";
import { CompositionPersistenceService, type ExportSnapshotClipRecord } from "./CompositionPersistenceService";
import { CompositionRestoreService } from "./CompositionRestoreService";
import type { ResolveAssetUrlFn, ResolveEnhancedAudioUrlFn } from "./CompositionSessionService";

type LiveExportSnapshot = {
    display?: {
        canvasWidth?: number;
        canvasHeight?: number;
        backgroundColor?: string;
    };
    orderedLayerIds?: string[];
    orderedClipIdsByLayerId?: Record<string, string[]>;
    layerMetaById?: Record<string, { name?: string | null; type?: string | null }>;
    clipRecordsById?: Record<string, ExportSnapshotClipRecord>;
};

function normalizeHexColor(value: any, fallback: hex = "#000000"): hex {
    const s = typeof value === "string" ? value.trim() : "";
    if (s.startsWith("#") && s.length > 1) return s as hex;
    return fallback;
}

async function waitForRenderer(comp: Composition, timeoutMs = 15000): Promise<void> {
    if (comp.renderer) return;

    await new Promise<void>((resolve, reject) => {
        let initListenerId: string | null = null;
        const timeoutId = window.setTimeout(() => {
            try {
                if (initListenerId) comp.off(initListenerId);
            } catch {}
            reject(new Error("Timed out waiting for export renderer to initialize"));
        }, timeoutMs);

        initListenerId = comp.on("init", () => {
            window.clearTimeout(timeoutId);
            try {
                if (initListenerId) comp.off(initListenerId);
            } catch {}
            resolve();
        });
    });
}

function mergeLiveSnapshotIntoPersistedLayers(persistedLayers: any[], liveSnapshot?: LiveExportSnapshot): any[] {
    const normalizedLayers: any[] = (Array.isArray(persistedLayers) ? persistedLayers : [])
        .map((layer: any) => ({
            layerId: String(layer?.layerId ?? ""),
            name: layer?.name ?? null,
            type: typeof layer?.type === "string" && layer.type ? String(layer.type) : "base",
            clips: Array.isArray(layer?.clips) ? [...layer.clips] : [],
        }))
        .filter((layer: any) => layer.layerId);

    if (!liveSnapshot) return normalizedLayers;

    const layerById = new Map<string, any>();
    for (const layer of normalizedLayers) {
        layerById.set(String(layer.layerId), layer);
    }

    const layerMetaById = liveSnapshot.layerMetaById;
    if (layerMetaById && typeof layerMetaById === "object") {
        for (const [layerIdRaw, meta] of Object.entries(layerMetaById)) {
            const layerId = String(layerIdRaw ?? "");
            if (!layerId) continue;
            const existing = layerById.get(layerId);
            if (!existing) continue;
            if (meta && Object.prototype.hasOwnProperty.call(meta, "name")) {
                existing.name = meta?.name ?? null;
            }
            if (meta && typeof meta?.type === "string" && meta.type) {
                existing.type = String(meta.type);
            }
        }
    }

    const clipRecordsById = liveSnapshot.clipRecordsById;
    if (clipRecordsById && typeof clipRecordsById === "object") {
        for (const rec of Object.values(clipRecordsById)) {
            if (!rec || typeof rec !== "object") continue;
            const clipId = String((rec as any).id ?? "");
            if (!clipId) continue;
            const layerId = String((rec as any).layerId ?? "");
            if (!layerId) continue;

            // Prevent duplicate clip IDs when a clip changed layers before persistence catches up.
            for (const layer of normalizedLayers) {
                if (String(layer.layerId) === layerId) continue;
                const idx = (layer.clips as any[]).findIndex((clip: any) => String(clip?.id ?? "") === clipId);
                if (idx >= 0) (layer.clips as any[]).splice(idx, 1);
            }

            let layer = layerById.get(layerId);
            if (!layer) {
                const fromMeta = layerMetaById?.[layerId];
                layer = {
                    layerId,
                    name: fromMeta?.name ?? null,
                    type:
                        typeof fromMeta?.type === "string" && fromMeta.type
                            ? String(fromMeta.type)
                            : "base",
                    clips: [],
                };
                normalizedLayers.push(layer);
                layerById.set(layerId, layer);
            }

            const existingIndex = (layer.clips as any[]).findIndex((clip: any) => String(clip?.id ?? "") === clipId);
            const merged = {
                ...(existingIndex >= 0 ? (layer.clips as any[])[existingIndex] : {}),
                ...(rec as any),
                id: clipId,
                layerId,
            };

            if (existingIndex >= 0) {
                (layer.clips as any[])[existingIndex] = merged;
            } else {
                (layer.clips as any[]).push(merged);
            }
        }
    }

    const orderByLayer = liveSnapshot.orderedClipIdsByLayerId;
    if (orderByLayer && typeof orderByLayer === "object") {
        for (const layer of normalizedLayers) {
            const orderRaw = (orderByLayer as any)[String(layer.layerId)];
            const order: string[] = Array.isArray(orderRaw) ? orderRaw.map((id: any) => String(id ?? "")).filter(Boolean) : [];
            if (!order.length) continue;

            const byId = new Map<string, any>();
            for (const clip of layer.clips as any[]) {
                const id = String(clip?.id ?? "");
                if (!id) continue;
                byId.set(id, clip);
            }

            const reordered: any[] = [];
            for (const clipId of order) {
                const clip = byId.get(clipId);
                if (!clip) continue;
                reordered.push(clip);
                byId.delete(clipId);
            }

            for (const clip of layer.clips as any[]) {
                const id = String(clip?.id ?? "");
                if (!id || !byId.has(id)) continue;
                reordered.push(clip);
                byId.delete(id);
            }

            layer.clips = reordered;
        }
    }

    const orderedLayerIds: string[] = Array.isArray(liveSnapshot.orderedLayerIds)
        ? liveSnapshot.orderedLayerIds.map((id: any) => String(id ?? "")).filter(Boolean)
        : [];
    if (!orderedLayerIds.length) {
        return normalizedLayers;
    }

    const ordered: any[] = [];
    const consumed = new Set<string>();

    for (const layerId of orderedLayerIds) {
        const layer = layerById.get(layerId);
        if (!layer) continue;
        ordered.push(layer);
        consumed.add(layerId);
    }

    for (const layer of normalizedLayers) {
        const layerId = String(layer.layerId ?? "");
        if (!layerId || consumed.has(layerId)) continue;
        ordered.push(layer);
    }

    return ordered;
}

function getDetachedScriptLayerClip(comp: any): any | null {
    try {
        const clipId = String(comp?.scriptLayer?.clipId ?? "");
        if (!clipId) return null;

        if (typeof comp?.getClipById === "function") {
            const clip = comp.getClipById(clipId);
            if (clip) return clip;
        }

        for (const tr of comp?.tracks ?? []) {
            for (const clip of tr?.clips ?? []) {
                if (String(clip?.id ?? "") === clipId) return clip;
            }
        }
    } catch {}

    return null;
}

function enforceDetachedExportVisualOrder(comp: Composition): void {
    const compAny: any = comp as any;
    const tracks: any[] = Array.isArray(compAny?.tracks) ? compAny.tracks : [];
    if (!tracks.length) return;

    const scriptTrackId = String(compAny?.scriptLayer?.layerId ?? "");
    const scriptTrackIndex = scriptTrackId
        ? tracks.findIndex((track: any) => String(track?.id ?? "") === scriptTrackId)
        : -1;

    if (scriptTrackIndex >= 0 && scriptTrackIndex !== tracks.length - 1) {
        const scriptTrack = tracks[scriptTrackIndex];
        let movedByTrackApi = false;
        try {
            if (typeof scriptTrack?.layer === "function") {
                scriptTrack.layer("bottom");
                movedByTrackApi = true;
            }
        } catch {}

        if (!movedByTrackApi) {
            tracks.splice(scriptTrackIndex, 1);
            tracks.push(scriptTrack);
        }
    }

    const stage = compAny?.stage;
    if (!stage) return;

    const TRACK_Z_STEP = 1000;
    const trackZ = (trackIndex: number): number => -trackIndex * TRACK_Z_STEP;
    const items: Array<{ child: any; z: number; kind: "track" | "scriptSource" }> = [];

    for (let i = 0; i < tracks.length; i += 1) {
        const track = tracks[i];
        if (track?.view && track.view.parent === stage) {
            items.push({ child: track.view, z: trackZ(i), kind: "track" });
        }
    }

    try {
        const scriptClip: any = getDetachedScriptLayerClip(compAny);
        if (scriptClip && scriptClip._videoContainers) {
            const scriptTrackIdForZ = String(compAny?.scriptLayer?.layerId ?? "");
            const scriptTrackIndexForZ = scriptTrackIdForZ
                ? tracks.findIndex((track: any) => String(track?.id ?? "") === scriptTrackIdForZ)
                : -1;
            const scriptBaseZ = scriptTrackIndexForZ >= 0 ? trackZ(scriptTrackIndexForZ) : trackZ(tracks.length);

            let sortedEntries: Array<{ entry: any }> = [];
            if (typeof scriptClip.getVideoEntriesSortedByLayer === "function") {
                const maybeEntries = scriptClip.getVideoEntriesSortedByLayer();
                if (Array.isArray(maybeEntries)) {
                    sortedEntries = maybeEntries as Array<{ entry: any }>;
                }
            } else if (typeof scriptClip.getVideoEntries === "function") {
                const maybeEntries = scriptClip.getVideoEntries();
                if (Array.isArray(maybeEntries)) {
                    sortedEntries = (maybeEntries as any[])
                        .map((entry) => ({ entry }))
                        .sort((a, b) => Number(a?.entry?.layerIndex ?? 0) - Number(b?.entry?.layerIndex ?? 0));
                }
            }

            for (const item of sortedEntries) {
                const entry = item?.entry;
                if (!entry) continue;
                const container = scriptClip._videoContainers.get?.(entry.input);
                if (!container || container.parent !== stage) continue;

                const rawLayerIndex = Number(entry.layerIndex ?? 0);
                const layerIndex = Number.isFinite(rawLayerIndex) ? rawLayerIndex : 0;
                items.push({ child: container, z: scriptBaseZ + layerIndex, kind: "scriptSource" });
            }
        }
    } catch {}

    items.sort((a, b) => {
        if (a.z !== b.z) return a.z - b.z;
        if (a.kind !== b.kind) return a.kind === "track" ? -1 : 1;
        return 0;
    });

    for (const { child } of items) {
        try {
            if (child?.parent === stage) stage.removeChild(child);
            stage.addChild(child);
        } catch {}
    }
}

export async function createDetachedExportComposition(args: {
    compositionId: string;
    resolveAssetUrl: ResolveAssetUrlFn;
    resolveEnhancedAudioUrl?: ResolveEnhancedAudioUrlFn;
    captionsWordsByClipId?: Record<string, any[]>;
    captionsConfigByClipId?: Record<string, any>;
    textConfigByClipId?: Record<string, any>;
    liveSnapshot?: LiveExportSnapshot;
}): Promise<Composition> {
    const [meta, { layers }] = await Promise.all([
        CompositionPersistenceService.fetchComposition(args.compositionId),
        CompositionPersistenceService.fetchClipsByLayerOrdered(args.compositionId),
    ]);

    const liveDisplay = args.liveSnapshot?.display;
    const width = Number(liveDisplay?.canvasWidth ?? meta?.canvasWidth ?? 1920);
    const height = Number(liveDisplay?.canvasHeight ?? meta?.canvasHeight ?? 1080);
    const background = normalizeHexColor(liveDisplay?.backgroundColor ?? meta?.backgroundColor, "#000000");

    const restoreMeta = {
        ...(meta ?? {}),
        canvasWidth: Number.isFinite(width) && width > 0 ? width : 1920,
        canvasHeight: Number.isFinite(height) && height > 0 ? height : 1080,
        backgroundColor: background,
    };

    const mergedLayers = mergeLiveSnapshotIntoPersistedLayers((layers as any[]) ?? [], args.liveSnapshot);

    const exportComp = new Composition({
        width: Number.isFinite(width) && width > 0 ? width : 1920,
        height: Number.isFinite(height) && height > 0 ? height : 1080,
        background,
        backend: "webgl",
        id: `export_${args.compositionId}`,
    });

    try {
        // Create a detached canvas for the export composition (never mounted in the UI).
        // The export encoder renders from the Pixi renderer canvas, but some clip paths assume a composition canvas exists.
        try {
            const canvas = document.createElement("canvas");
            exportComp.attachCanvasToComposition(canvas);
        } catch {}

        await waitForRenderer(exportComp);

        await CompositionRestoreService.restoreIntoCompositionFromSnapshot({
            comp: exportComp,
            meta: restoreMeta,
            layers: mergedLayers,
            resolveAssetUrl: args.resolveAssetUrl,
            resolveEnhancedAudioUrl: args.resolveEnhancedAudioUrl,
            // Avoid mutating editor/global UI stores during export restore.
            writeToStateStore: false,
        });

        // Captions clip word timing data is derived from transcripts and not persisted.
        // Seed it from the active editor composition (captured before export) so exports include captions.
        try {
            const map = args.captionsWordsByClipId;
            if (map && typeof map === "object") {
                for (const tr of (exportComp as any).tracks ?? []) {
                    for (const c of (tr as any)?.clips ?? []) {
                        if (!c || String(c.type) !== "captions") continue;
                        const clipId = String(c.id ?? "");
                        if (!clipId) continue;
                        const existing = (c as any).words;
                        const hasWords = Array.isArray(existing) && existing.length > 0;
                        if (hasWords) continue;
                        const seed = map[clipId];
                        if (!Array.isArray(seed) || seed.length === 0) continue;
                        try {
                            (c as any).words = seed.map((w: any) => ({ ...(w ?? {}) }));
                        } catch {}
                    }
                }
            }
        } catch {}

        // Captions config may not have flushed to Firestore yet (toolbar edits, per-scene overrides, etc.).
        // Override the restored captions config with the active editor's snapshot so export matches preview.
        try {
            const cfgMap = args.captionsConfigByClipId;
            if (cfgMap && typeof cfgMap === "object") {
                for (const tr of (exportComp as any).tracks ?? []) {
                    for (const c of (tr as any)?.clips ?? []) {
                        if (!c || String(c.type) !== "captions") continue;
                        const clipId = String(c.id ?? "");
                        if (!clipId) continue;
                        const seed = (cfgMap as any)[clipId];
                        if (!seed || typeof seed !== "object") continue;

                        // Apply any private sceneStyleOverrides first so set() recomputes effective style correctly.
                        try {
                            const ovs = (seed as any).sceneStyleOverrides;
                            if (ovs && typeof ovs === "object") {
                                (c as any)._sceneStyleOverrides = JSON.parse(JSON.stringify(ovs));
                            }
                        } catch {}

                        try {
                            const props: any = {};
                            for (const k of [
                                "scriptClipId",
                                "timingScope",
                                "displayMode",
                                "textAlign",
                                "textBaseline",
                                "textCase",
                                "maxWidth",
                                "textStyle",
                                "highlightStyle",
                                "background",
                                "phraseConfig",
                                // Visual props (best-effort so export matches canvas preview even if not yet persisted)
                                "position",
                                "width",
                                "height",
                                "scale",
                                "rotation",
                                "alpha",
                            ]) {
                                if (!(k in seed)) continue;
                                const v = (seed as any)[k];
                                if (v === undefined || v === null) continue;
                                props[k] = v;
                            }

                            // Prefer the clip's `set()` helper when available.
                            if (typeof (c as any).set === "function") {
                                (c as any).set(props);
                            } else {
                                Object.assign(c as any, props);
                            }
                        } catch {}
                    }
                }
            }
        } catch {}

        // Text clip content/style may not have flushed to Firestore yet (live editing overlay).
        // Override the restored TextClip config with the active editor's snapshot so export matches preview.
        try {
            const cfgMap = args.textConfigByClipId;
            if (cfgMap && typeof cfgMap === "object") {
                for (const tr of (exportComp as any).tracks ?? []) {
                    for (const c of (tr as any)?.clips ?? []) {
                        if (!c || String(c.type) !== "text") continue;
                        const clipId = String(c.id ?? "");
                        if (!clipId) continue;
                        const seed = (cfgMap as any)[clipId];
                        if (!seed || typeof seed !== "object") continue;

                        try {
                            const ovs = (seed as any).sceneStyleOverrides;
                            if (ovs && typeof ovs === "object") {
                                (c as any)._sceneStyleOverrides = JSON.parse(JSON.stringify(ovs));
                            }
                        } catch {}

                        try {
                            const props: any = {};
                            for (const k of [
                                "text",
                                "textAlign",
                                "textBaseline",
                                "textCase",
                                "maxWidth",
                                "textStyle",
                                // Visual props (best-effort so export matches canvas preview even if not yet persisted)
                                "position",
                                "width",
                                "height",
                                "scale",
                                "rotation",
                                "alpha",
                            ]) {
                                if (!(k in seed)) continue;
                                const v = (seed as any)[k];
                                if (v === undefined || v === null) continue;
                                props[k] = v;
                            }

                            if (typeof (c as any).set === "function") {
                                (c as any).set(props);
                            } else {
                                Object.assign(c as any, props);
                            }
                        } catch {}
                    }
                }
            }
        } catch {}

        // Match preview visual stacking semantics in detached export (track order + script source z-order).
        try {
            enforceDetachedExportVisualOrder(exportComp);
            (exportComp as any)?.computeFrame?.();
        } catch {}

        return exportComp;
    } catch (e) {
        try {
            await exportComp.dispose();
        } catch {}
        throw e;
    }
}
